/**
 * Agent Builder Copilot on the BotCircuits runtime (copilot_native).
 *
 * The copilot is an ordinary BotCircuits app (botcircuits-agent-builder-copilot-native),
 * reached through the runtime's standard web channel — the same pubsub API the web plugin
 * and the agent preview use — with that app's access key:
 *
 *   sendUserMessage(copilotApp, sessionId, {inputText, requestAttributes})
 *   subscribeBotMessage          replies, quick replies, approval buttons
 *   subscribeRuntimeDebugNotify  tool / plan / approval events (agentEvent)
 *
 * The application being built travels as request attributes (`appId`, or `accountId`
 * outside an application); the copilot's tools read it from there, so the model never
 * chooses the app. So does the signed-in user's token (`token`): the tools call the
 * BotCircuits API with it, so the copilot can do exactly what this user can. model/runtime-events.js turns the runtime's output into the panel's
 * event protocol, the one pages/copilot renders.
 *
 * Every call names a scope in `appId`, like pages/copilot: the application the user is in,
 * or `account:<accountId>` outside any application.
 */
import { Auth } from 'aws-amplify';
import config from '../../../config';
import { loadAccessKey } from '../../../services/auth/on-prem-auth-service';
import { graphqlRequest, subscribe } from './appsync-client';
import { createRuntimeTranslator, inputFor, transcriptRows } from '../model/runtime-events';

export const COPILOT_APP_ID = config.COPILOT_NATIVE_APP_ID;
const COPILOT_ACCESS_KEY = config.COPILOT_NATIVE_ACCESS_KEY;
const PUBSUB_URL = config.REALTIME_API_BASE_URL;

export const isCopilotConfigured = () => Boolean(COPILOT_APP_ID && COPILOT_ACCESS_KEY && PUBSUB_URL);

export const ACCOUNT_SCOPE_PREFIX = 'account:';

const SEND_USER_MESSAGE = /* GraphQL */ `
    mutation Publish($data: AWSJSON!, $appId: String!, $sessionId: String!) {
        sendUserMessage(data: $data, appId: $appId, sessionId: $sessionId) { data appId sessionId }
    }
`;

const ON_BOT_MESSAGE = /* GraphQL */ `
    subscription Subscribe($appId: String!, $sessionId: String!) {
        subscribeBotMessage(appId: $appId, sessionId: $sessionId) { data appId sessionId }
    }
`;

const ON_DEBUG_NOTIFY = /* GraphQL */ `
    subscription Subscribe($appId: String!, $sessionId: String!) {
        subscribeRuntimeDebugNotify(appId: $appId, sessionId: $sessionId) { data appId sessionId }
    }
`;

/**
 * AWSJSON fields arrive as JSON text, sometimes encoded twice. Decode until it's a value.
 */
export function decodeJson(value) {
    let current = value;
    for (let i = 0; i < 3 && typeof current === 'string'; i += 1) {
        try {
            current = JSON.parse(current);
        } catch (_) {
            return null;
        }
    }
    return current;
}

/** Request attributes that tell the copilot's tools which application (or account) this is. */
export function scopeAttributes(scope) {
    if (typeof scope === 'string' && scope.startsWith(ACCOUNT_SCOPE_PREFIX)) {
        return { accountId: scope.slice(ACCOUNT_SCOPE_PREFIX.length) };
    }
    return { appId: scope };
}

// The copilot's tools keep using the token sent with a message for the whole turn, and a
// turn can run up to the runtime's 15-minute limit. Amplify only refreshes a token once it
// has expired, so one with less life left than this is refreshed before it is sent.
export const MIN_TOKEN_LIFETIME_SEC = 16 * 60;

function refreshSession(user, refreshToken) {
    return new Promise((resolve, reject) => {
        user.refreshSession(refreshToken, (error, session) => (error ? reject(error) : resolve(session)));
    });
}

async function cognitoSession() {
    const session = await Auth.currentSession();
    const expiresAt = session.getAccessToken().getExpiration();
    if (expiresAt - Date.now() / 1000 >= MIN_TOKEN_LIFETIME_SEC) return session;
    try {
        const user = await Auth.currentAuthenticatedUser();
        return await refreshSession(user, session.getRefreshToken());
    } catch (_) {
        // Still valid for now; the turn may outlive it, but sending it beats failing outright.
        return session;
    }
}

/**
 * The signed-in user's token, the same one the platform sends to its REST API (Cognito
 * access token; the stored access key on-prem). Fetched per message and refreshed when it
 * would expire within MIN_TOKEN_LIFETIME_SEC, so a long turn never outlives it.
 */
export async function getUserToken() {
    let token = null;
    try {
        token = config.IS_ON_PREM
            ? loadAccessKey()
            : (await cognitoSession()).getAccessToken().getJwtToken();
    } catch (_) {
        token = null;
    }
    if (!token) throw new Error('Your session has expired. Sign in again to use the copilot.');
    return token;
}

/**
 * Whether the signed-in user's account may still use the copilot this billing period
 * (Free plan AI usage cap): `{ blocked, message }`, or null when it can't be told (on-prem,
 * signed out, billing unreachable) — the copilot then just carries on; the runtime checks
 * the cap itself before each turn.
 */
export async function getAiUsageStatus() {
    if (config.IS_ON_PREM) return null;
    try {
        const token = await getUserToken();
        const response = await fetch(`${config.API_BASE_URL}/billing/usage/ai/status`, {
            headers: { Authorization: token, 'Content-Type': 'application/json' },
        });
        if (!response.ok) return null;
        const status = await response.json();
        return {
            blocked: status?.aiUsageBillingStatus === false,
            message: typeof status?.message === 'string' ? status.message : null,
        };
    } catch (_) {
        return null;
    }
}

/**
 * `answer` ({slot, value}) answers a playbook card with data: the runtime fills that variable
 * directly (requestAttributes actionType `slot_answer`), with no model call; `message` is then
 * only what the transcript shows.
 */
export async function sendCopilotMessage(scope, sessionId, { message, approval, previewSessionId, answer } = {}, subscription = null) {
    const token = await getUserToken();
    subscription?.translator?.startRun();
    const data = {
        action: 'executor',
        appId: COPILOT_APP_ID,
        sessionId,
        inputText: inputFor({ message, approval }),
        requestAttributes: {
            ...scopeAttributes(scope), token, previewSessionId: previewSessionId || null,
            ...(answer ? { actionType: 'slot_answer', slot: answer.slot, slotValue: answer.value } : {}),
        },
    };
    recordSession(scope, sessionId, message);
    await graphqlRequest(PUBSUB_URL, COPILOT_ACCESS_KEY, SEND_USER_MESSAGE, {
        appId: COPILOT_APP_ID, sessionId, data: JSON.stringify(data),
    });
    return { sessionId, status: 'queued' };
}

// ---------------------------------------------------------------------------
// Sessions: the copilot app is shared, so this browser keeps its own index per scope.
// ---------------------------------------------------------------------------
const indexKey = scope => `bc-copilot-native:${scope}:sessions`;
const MAX_SESSIONS = 30;

function readIndex(scope) {
    try {
        const items = JSON.parse(window.localStorage.getItem(indexKey(scope)) || '[]');
        return Array.isArray(items) ? items : [];
    } catch (_) {
        return [];
    }
}

export function recordSession(scope, sessionId, message) {
    try {
        const now = new Date().toISOString();
        const items = readIndex(scope);
        const existing = items.find(item => item.sessionId === sessionId);
        const rest = items.filter(item => item.sessionId !== sessionId);
        const entry = {
            sessionId,
            title: existing?.title || (message ? String(message).slice(0, 80) : 'New chat'),
            lastMessage: message ? String(message).slice(0, 200) : existing?.lastMessage || '',
            createdAt: existing?.createdAt || now,
            updatedAt: now,
        };
        window.localStorage.setItem(indexKey(scope), JSON.stringify([entry, ...rest].slice(0, MAX_SESSIONS)));
    } catch (_) {
        // storage unavailable: the session just won't be listed
    }
}

export async function listCopilotSessions(scope) {
    return readIndex(scope);
}

export async function getCopilotSession(scope, sessionId) {
    const url = `${config.API_BASE_URL}/apps/${COPILOT_APP_ID}/sessions/${encodeURIComponent(sessionId)}/messages`;
    const response = await fetch(url, { headers: { Authorization: COPILOT_ACCESS_KEY, 'Content-Type': 'application/json' } });
    if (!response.ok) throw new Error(`Could not load the conversation (${response.status}).`);
    const body = await response.json().catch(() => ({}));
    const { rows, status } = transcriptRows(body?.data || body?.messages || []);
    return { session: { status }, messages: rows };
}

/**
 * The session's latest turn as the runtime recorded it: `{ turn: {turnId, status}, events }`,
 * where status is 'running' while the copilot is still working on it (the page was reloaded
 * or left mid-turn). Null when it can't be read: the page then shows the transcript only.
 */
export async function getCopilotProgress(sessionId) {
    try {
        const url = `${config.API_BASE_URL}/apps/${COPILOT_APP_ID}/runtime/sessions/${encodeURIComponent(sessionId)}/progress`;
        const response = await fetch(url, { headers: { Authorization: COPILOT_ACCESS_KEY, 'Content-Type': 'application/json' } });
        if (!response.ok) return null;
        const body = await response.json();
        const turn = body?.turn;
        if (!turn || typeof turn.turnId !== 'string' || !turn.turnId) return null;
        return { turn, events: Array.isArray(body.events) ? body.events : [] };
    } catch (_) {
        return null;
    }
}

/** Subscribe to a session's replies and agent events; `onEvent` receives panel events. */
export async function subscribeCopilotEvents(scope, sessionId, { onEvent, onError, onStatus }) {
    const translator = createRuntimeTranslator(onEvent);
    const variables = { appId: COPILOT_APP_ID, sessionId: sessionId };
    const replies = subscribe(PUBSUB_URL, COPILOT_ACCESS_KEY, ON_BOT_MESSAGE, variables, {
        onError,
        onStatus,
        onData: data => {
            const payload = decodeJson(data?.subscribeBotMessage?.data);
            if (payload && typeof payload === 'object') translator.onBotMessage(payload);
        },
    });
    const progress = subscribe(PUBSUB_URL, COPILOT_ACCESS_KEY, ON_DEBUG_NOTIFY, variables, {
        // Progress is best effort: losing it must not fail the conversation.
        onData: data => {
            const payload = decodeJson(data?.subscribeRuntimeDebugNotify?.data);
            if (payload && typeof payload === 'object') translator.onDebugNotify(payload);
        },
    });
    return {
        ready: Promise.all([replies.ready, progress.ready.catch(() => undefined)]).then(() => undefined),
        translator,
        close() {
            translator.dispose();
            replies.close();
            progress.close();
        },
    };
}
